import React from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Home, Calendar, DollarSign, Settings, LogOut, Menu, Users, Tag, Mail, CreditCard, RefreshCw, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const AdminLayout = ({ children }) => {
  const { logout } = useAuth();

  const navItems = [
    { path: "/admin/dashboard", label: "Dashboard", icon: Home },
    { path: "/admin/property", label: "Property", icon: Settings },
    { path: "/admin/pricing", label: "Pricing", icon: DollarSign },
    { path: "/admin/bookings", label: "Bookings", icon: Calendar },
    { path: "/admin/discount-codes", label: "Afsláttarkóðar", icon: Tag },
    { path: "/admin/users", label: "Notendur", icon: Users },
    { path: "/admin/email-settings", label: "Tölvupóststillingar", icon: Mail },
    { path: "/admin/payment-settings", label: "Greiðslur", icon: CreditCard },
    { path: "/admin/calendar-sync", label: "Dagatal", icon: RefreshCw },
    { path: "/admin/documents", label: "Skjöl", icon: FileText },
  ];

  const Sidebar = () => (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b border-neutral-200">
        <h2 className="text-xl font-display font-bold text-primary-600">Admin Panel</h2>
      </div>
      <nav className="flex-1 p-4" data-testid="admin-sidebar">
        <div className="space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg font-ui transition-colors ${
                    isActive
                      ? "bg-primary-50 text-primary border-l-4 border-primary"
                      : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
      <div className="p-4 border-t border-neutral-200">
        <Button
          onClick={logout}
          variant="ghost"
          className="w-full justify-start text-red-600 hover:bg-red-50 hover:text-red-700"
          data-testid="logout-button"
        >
          <LogOut className="w-5 h-5 mr-3" />
          Logout
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-neutral-200 p-4 flex items-center justify-between">
        <h2 className="text-xl font-display font-bold text-primary-600">Admin Panel</h2>
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu className="w-6 h-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0">
            <Sidebar />
          </SheetContent>
        </Sheet>
      </div>

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:block w-64 bg-white border-r border-neutral-200 min-h-screen">
          <Sidebar />
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 md:p-8 lg:p-12">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;